<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import StatusTag from '@/components/table/StatusTag.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { formatedDate } from '@/constants'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import {getRollsList} from "@/api/rolls";
import type {Roll} from "@/types/roll-types";
import {getRollState } from "@/constants/rolls";
import { getProductList } from '@/api/products'
import type {Product} from "@/types/product-types";
import {getBatchById, getBatchList, getReturnedBatchList} from "@/api/batches";
import type {Batch, ReturnBatch} from "@/types/batch-types";
import {getBatchRoutePath, navigateToBatchDetails} from "@/utils/batch-navigation";

const router = useRouter()
const rollList = ref<Roll[]>([])
const loading = ref<boolean>(false)
const searchQuery = ref('')
const productsList = ref<Product[]>([])
const batchList = ref<Batch[]>([])
const returnedBatchList = ref<ReturnBatch[]>([])
const statusFilters = ref<string[]>([])

const rollFilterOptions = [
  { value: 'all', label: 'All', id: 'roll-status-all' },
  { value: 'greig', label: 'Greig', id: 'roll-status-greig' },
  { value: 'offsite', label: 'Offsite', id: 'roll-status-offsite' },
  { value: 'finished', label: 'Finished', id: 'roll-status-finished' },
  { value: 'available', label: 'Available', id: 'roll-status-available' },
  { value: 'dispatched', label: 'Dispatched', id: 'roll-status-dispatched' }
]

const filteredRollList = computed(() => {
  const query = searchQuery.value ? searchQuery.value.toLowerCase() : "";

  let filtered = rollList.value.filter((roll: Roll) => {
    const rollState = getRollState(roll.rollState) || "";
    const rollNumber = roll.rollNumber || "";

    return rollState.toLowerCase().includes(query) || rollNumber.toLowerCase().includes(query);
  });

  if (statusFilters.value.length > 0 && !statusFilters.value.includes('all')) {
    filtered = filtered.filter((roll: Roll) => {
      const rollState = getRollState(roll.rollState);
      if (!rollState) return false;

      const rollStateValue = rollState.toLowerCase();
      return statusFilters.value.includes(rollStateValue);
    });
  }

  return filtered;
});

const getQuality = (roll: Roll) => {
  const productId = roll.finishingQualityId || roll.knittingQualityId
  const product = productsList.value.find((product: Product) => product.id === productId)

  if (!product) return { id: null, name: 'Unknown Quality' }

  return {
    id: productId,
    name: roll.finishingQualityId ? product.finishedReference : product.unfinishedReference
  }
}

const getBatchNumber = (roll: Roll) => {
  const batch = batchList.value.find((batch: Batch) => batch.id === roll.batchId)

  if (!batch) return 'Unknown Batch'

  if (batch.returnedBatchId) {
    const returnedBatch = returnedBatchList.value.find((rb: any) => rb.id === batch.returnedBatchId)
    if (returnedBatch) {
      return returnedBatch.batchNumber || batch.batchNumber
    }
  }

  return batch.batchNumber
}

const getBatchRoute = (roll: Roll) => {
  const batch = batchList.value.find((batch: Batch) => batch.id === roll.batchId)
  return getBatchRoutePath(batch);
}

const handleFilterChange = (filters: string[]) => {
  statusFilters.value = filters
}

onMounted(async () => {
  loading.value = true;
  rollList.value = await getRollsList();
  productsList.value = await getProductList()
  batchList.value = await getBatchList()
  returnedBatchList.value = await getReturnedBatchList()
  loading.value = false;
})
</script>

<template>
  <PageHeader
      title="Rolls"
      :description="`Total: ${rollList.length} | Filtered: ${filteredRollList.length}`"
      buttonText="Send to Supplier"
      buttonIcon="mdi:send"
      :buttonClick="() => router.push('/rolls/send-to-supplier')"
  />

  <DataTable
      :value="filteredRollList"
      paginator
      :rows="10"
      tableStyle="min-width: 50rem"
      stripedRows
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      :rowsPerPageOptions="[10, 25]"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} rolls"
      :loading="loading"
      :pt="{
      table: loading && 'min-h-[30rem]'
    }"
      :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No rolls found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

        <TableFilter
            :filter-options="rollFilterOptions"
            filter-title="Filter Roll Status"
            @filter-change="handleFilterChange"
        />
      </div>
    </template>

    <Column field="rollNumber" header="Roll Number">
      <template #body="slotProps">
        <RouterLink
            :to="`/rolls/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.rollNumber }}
        </RouterLink>
      </template>
    </Column>

    <Column field="qualityId" header="Quality">
      <template #body="slotProps">
        <RouterLink
            v-if="getQuality(slotProps.data).id"
            :to="`/productSpecification/${getQuality(slotProps.data).id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getQuality(slotProps.data).name }}
        </RouterLink>
        <span v-else>{{ getQuality(slotProps.data).name }}</span>
      </template>
    </Column>

    <Column field="batchId" header="Batch">
      <template #body="slotProps">
        <RouterLink
            :to="getBatchRoute(slotProps.data)"
            class="font-medium text-neutral-800 hover:underline"
        >
          {{ getBatchNumber(slotProps.data) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="createdAt" header="Date knitted">
      <template #body="slotProps">
        {{ formatedDate(slotProps.data.dateKnitted) }}
      </template>
    </Column>

    <Column field="rollState" header="Status">
      <template #body="slotProps">
        <StatusTag
            :status="getRollState(slotProps.data.rollState)"
        />
      </template>
    </Column>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
            :to="`/rolls/${slotProps.data.id}`"
            class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span>More Details</span>
          <Icon
              icon="material-symbols:more-up"
              class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>

<style></style>
